import React from 'react';
import { isFileReferenceUrl } from '../../../shared/FileLink';
import { useResolvedFileUrl } from '../../../shared/useResolvedFileUrl';
import {
  ConfirmationEmailCandidate,
  ConfirmationEmailSearch,
  EMPTY_EMAIL_SEARCH,
  ProofImageCandidate,
  ProofSelection,
} from './closeTaskProof';

/**
 * Tick-boxes for the proof the operator passes on when closing a task. The
 * payload / validation logic lives in closeTaskProof.ts.
 */

interface CloseTaskProofPickerProps {
  value: ProofSelection;
  onChange: (next: ProofSelection) => void;
  /** Images already in the task chat plus images attached on this form. */
  images: ProofImageCandidate[];
  /** Matching confirmation emails, looked up when the option is ticked. */
  emails?: ConfirmationEmailSearch;
  /** Phone-call tasks: the time is taken from the call log. */
  isPhoneCall?: boolean;
  disabled?: boolean;
}

type OptionKey = keyof ProofSelection;

const OPTIONS: { key: OptionKey; label: string; desc: string }[] = [
  {
    key: 'screenshots',
    label: 'Screenshots',
    desc: 'Of the confirmation page or message — tick all that apply',
  },
  {
    key: 'reference',
    label: 'Reference number',
    desc: 'Booking, order or confirmation number',
  },
  {
    key: 'person',
    label: 'Spoke to someone',
    desc: 'Name of the person who confirmed it',
  },
  {
    key: 'email',
    label: 'Confirmation email',
    desc: "Sent to the user — we'll look for it in their inbox for you to tick",
  },
  {
    key: 'nothing',
    label: 'Nothing to attach',
    desc: 'Done, but they gave nothing the user could check',
  },
];

function toggle(sel: ProofSelection, key: OptionKey): ProofSelection {
  const on = !sel[key].on;
  const next: ProofSelection = {
    ...sel,
    [key]: { ...sel[key], on },
  } as ProofSelection;
  // "Nothing to attach" cannot sit alongside actual proof.
  if (key === 'nothing' && on) {
    next.screenshots = { ...next.screenshots, on: false };
    next.reference = { ...next.reference, on: false };
    next.person = { ...next.person, on: false };
    next.email = { ...next.email, on: false };
  } else if (key !== 'nothing' && on) {
    next.nothing = { ...next.nothing, on: false };
  }
  return next;
}

const ProofThumb: React.FC<{
  image: ProofImageCandidate;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}> = ({ image, selected, disabled, onToggle }) => {
  const needsResolve = !image.previewUrl && !!image.url;
  const resolved = useResolvedFileUrl(
    needsResolve && image.url && isFileReferenceUrl(image.url)
      ? image.url
      : (image.previewUrl ?? image.url ?? ''),
  );
  const src =
    image.previewUrl ?? (resolved.status === 'ready' ? resolved.src : '');
  return (
    <button
      type='button'
      role='checkbox'
      aria-checked={selected}
      aria-label={image.name}
      disabled={disabled || image.status !== 'success'}
      className={`proof-image${selected ? ' selected' : ''}`}
      onClick={onToggle}
      title={image.name}
    >
      {src ? (
        <img src={src} alt={image.name} />
      ) : (
        <span className='proof-image-name'>{image.name}</span>
      )}
      <span className='proof-tick' aria-hidden='true'>
        {selected ? '✓' : ''}
      </span>
      {image.status === 'uploading' && (
        <span className='proof-image-status'>Uploading…</span>
      )}
    </button>
  );
};

function formatReceived(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "Can Pau <reservas@canpau.pt>" → "Can Pau". */
function senderName(from: string): string {
  const match = from.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/);
  return match ? match[1].trim() : from.trim();
}

const EmailCandidate: React.FC<{
  email: ConfirmationEmailCandidate;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}> = ({ email, selected, disabled, onToggle }) => (
  <label className={`proof-email${selected ? ' selected' : ''}`}>
    <input
      type='checkbox'
      checked={selected}
      disabled={disabled}
      onChange={onToggle}
    />
    <span className='proof-email-body'>
      <span className='proof-email-head'>
        <span className='proof-email-from'>{senderName(email.from)}</span>
        <span className='proof-email-time'>
          {formatReceived(email.receivedAt)}
        </span>
      </span>
      <span className='proof-email-subject'>{email.subject}</span>
      {email.snippet && (
        <span className='proof-email-snippet'>{email.snippet}</span>
      )}
    </span>
  </label>
);

const CloseTaskProofPicker: React.FC<CloseTaskProofPickerProps> = ({
  value,
  onChange,
  images,
  emails = EMPTY_EMAIL_SEARCH,
  isPhoneCall = false,
  disabled = false,
}) => {
  const chat = images.filter((i) => i.source === 'chat');
  const attached = images.filter((i) => i.source === 'attachment');
  const pickedCount = value.screenshots.keys.filter((k) =>
    images.some((i) => i.key === k),
  ).length;

  const toggleImage = (key: string) => {
    const keys = value.screenshots.keys.includes(key)
      ? value.screenshots.keys.filter((k) => k !== key)
      : [...value.screenshots.keys, key];
    onChange({ ...value, screenshots: { on: true, keys } });
  };

  const toggleEmail = (id: string) => {
    const ids = value.email.ids.includes(id)
      ? value.email.ids.filter((k) => k !== id)
      : [...value.email.ids, id];
    onChange({ ...value, email: { on: true, ids } });
  };
  const emailCount = value.email.ids.filter((id) =>
    emails.candidates.some((e) => e.messageId === id),
  ).length;

  const renderEmails = () => {
    if (emails.status === 'loading' || emails.status === 'idle') {
      return <div className='proof-hint'>Checking the user&apos;s inbox…</div>;
    }
    if (emails.status === 'error') {
      return (
        <div className='proof-hint'>
          Couldn&apos;t check the inbox just now. If a confirmation arrives in
          the next two days it is attached to this task automatically.
        </div>
      );
    }
    if (emails.unavailable) {
      return <div className='proof-hint'>{emails.unavailable}</div>;
    }
    if (emails.candidates.length === 0) {
      return (
        <div className='proof-hint'>
          No matching confirmation in the inbox yet. If one arrives in the next
          two days it is attached to this task automatically.
        </div>
      );
    }
    return (
      <>
        <div className='proof-group-title'>
          {emails.candidates.length === 1
            ? 'Found in the user’s inbox'
            : `Found in the user’s inbox — tick the right one${emails.candidates.length > 2 ? '(s)' : ''}`}
        </div>
        <div className='proof-emails'>
          {emails.candidates.map((email) => (
            <EmailCandidate
              key={email.messageId}
              email={email}
              selected={value.email.ids.includes(email.messageId)}
              disabled={disabled}
              onToggle={() => toggleEmail(email.messageId)}
            />
          ))}
        </div>
      </>
    );
  };

  const renderGroup = (title: string, list: ProofImageCandidate[]) =>
    list.length > 0 && (
      <div className='proof-group'>
        <div className='proof-group-title'>{title}</div>
        <div className='proof-images'>
          {list.map((img) => (
            <ProofThumb
              key={img.key}
              image={img}
              selected={value.screenshots.keys.includes(img.key)}
              disabled={disabled}
              onToggle={() => toggleImage(img.key)}
            />
          ))}
        </div>
      </div>
    );

  return (
    <div className='proof-section'>
      <label className='field-label'>
        Anything the user can check?{' '}
        <span className='optional'>(optional)</span>
      </label>
      <div className='proof-intro'>
        Only when you&apos;re telling the user something is booked, ordered or
        arranged. Tick everything you have — leaving this empty does not mean
        the task failed.
      </div>
      <div className='reason-options proof-options'>
        {OPTIONS.map((opt) => {
          const selected = value[opt.key].on;
          return (
            <label
              key={opt.key}
              className={`reason-option proof-option${selected ? ' selected' : ''}`}
            >
              <input
                type='checkbox'
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(toggle(value, opt.key))}
              />
              <span className='reason-content'>
                <span className='reason-label'>
                  {opt.label}
                  {opt.key === 'screenshots' && selected && pickedCount > 0 && (
                    <span className='proof-count'>{pickedCount} picked</span>
                  )}
                  {opt.key === 'email' && selected && emailCount > 0 && (
                    <span className='proof-count'>{emailCount} picked</span>
                  )}
                </span>
                <span className='reason-desc'>{opt.desc}</span>
              </span>
            </label>
          );
        })}
      </div>

      {value.screenshots.on && (
        <div className='proof-detail'>
          {images.length === 0 ? (
            <div className='proof-hint'>
              No images yet. Paste the screenshot into the task chat, or add it
              under Attachments above — it will show up here to tick.
            </div>
          ) : (
            <>
              {renderGroup('Already in the task chat', chat)}
              {renderGroup('Attached on this form', attached)}
              <div className='proof-hint'>
                Tap each screenshot to include it. Need another? Add it under
                Attachments above.
              </div>
            </>
          )}
        </div>
      )}

      {value.reference.on && (
        <div className='proof-detail proof-fields'>
          <input
            type='text'
            className='proof-input'
            value={value.reference.ref}
            disabled={disabled}
            placeholder='Reference number'
            onChange={(e) =>
              onChange({
                ...value,
                reference: { ...value.reference, ref: e.target.value },
              })
            }
          />
          <input
            type='text'
            className='proof-input'
            value={value.reference.issuer}
            disabled={disabled}
            placeholder='Issued by (optional)'
            onChange={(e) =>
              onChange({
                ...value,
                reference: { ...value.reference, issuer: e.target.value },
              })
            }
          />
        </div>
      )}

      {value.person.on && (
        <div className='proof-detail'>
          <input
            type='text'
            className='proof-input'
            value={value.person.name}
            disabled={disabled}
            placeholder='Who confirmed it, e.g. Rui at the front desk'
            onChange={(e) =>
              onChange({ ...value, person: { on: true, name: e.target.value } })
            }
          />
          <div className='proof-hint'>
            {isPhoneCall
              ? 'The time is recorded automatically: the end of the call, or the moment you close if there was no call.'
              : 'The time is recorded automatically when you close.'}
          </div>
        </div>
      )}

      {value.email.on && <div className='proof-detail'>{renderEmails()}</div>}

      {value.nothing.on && (
        <div className='proof-detail'>
          <input
            type='text'
            className='proof-input'
            value={value.nothing.reason}
            disabled={disabled}
            placeholder="Why nothing, e.g. they didn't give a name or reference"
            onChange={(e) =>
              onChange({
                ...value,
                nothing: { on: true, reason: e.target.value },
              })
            }
          />
        </div>
      )}
    </div>
  );
};

export default CloseTaskProofPicker;
